/**
 * Lightweight translation support. The production SPA is locked to English.
 * Existing Chinese entries remain available to isolated fixtures and manuscript tools.
 *
 * Design goals (matching this codebase):
 *   - No i18n library, no build step. Strings live next to the component that
 *     uses them as `Entry` literals ({ zh, en }); a component calls `useTr()`
 *     and renders `tr(S.someKey)`. Truly shared copy lives in `./common.ts`.
 *   - The default is English regardless of old preferences or browser language.
 *     An explicit locale is supported for fixtures; the app root locks English.
 *     This controls interface copy only, never author-written story content.
 *   - SSR-safe: the unit tests render via react-dom/server in a node env with
 *     no window/document/localStorage/navigator, so every browser global is
 *     guarded behind a typeof check.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/** The two supported UI languages. */
export type Locale = "zh" | "en";

/** A single translatable string in both languages. */
export interface Entry {
  readonly zh: string;
  readonly en: string;
}

/** Interpolation variables for `{name}`-style placeholders. */
export type TrVars = Record<string, string | number>;

const STORAGE_KEY = "lunaria.locale";

/**
 * Resolve `entry` to `locale`, then substitute `{key}` placeholders from
 * `vars`. Pure — safe to use outside React (e.g. document.title).
 */
export function translate(entry: Entry, locale: Locale, vars?: TrVars): string {
  let out = entry[locale] ?? entry.zh;
  if (vars) {
    for (const [key, value] of Object.entries(vars)) {
      out = out.replace(new RegExp(`\\{${key}\\}`, "g"), String(value));
    }
  }
  return out;
}

interface LocaleContextValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** English is the product language; explicit locales are for fixtures and tools. */
export function resolveInitialLocale(explicit?: Locale): Locale {
  return explicit ?? "en";
}

function applyDocumentLang(locale: Locale): void {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  }
}

export interface LocaleProviderProps {
  readonly children: ReactNode;
  /** An initial locale for fixtures; defaults to English. */
  readonly initial?: Locale;
  /** Lock production UI to a locale, including programmatic locale changes. */
  readonly locked?: Locale;
}

export function LocaleProvider({ children, initial, locked }: LocaleProviderProps) {
  const [selectedLocale, setLocaleState] = useState<Locale>(() => resolveInitialLocale(locked ?? initial));
  const locale = locked ?? selectedLocale;

  const setLocale = useCallback((requested: Locale) => {
    const next = locked ?? requested;
    setLocaleState(next);
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyDocumentLang(next);
  }, [locked]);

  useEffect(() => {
    applyDocumentLang(locale);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Read the current locale + setter. Falls back to a zh, no-op context when no
 * provider is mounted (keeps isolated component tests trivial).
 */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext) ?? { locale: "zh", setLocale: () => {} };
}

/** A translator bound to the current locale: `tr(entry, vars?) → string`. */
export function useTr(): (entry: Entry, vars?: TrVars) => string {
  const { locale } = useLocale();
  return useCallback((entry: Entry, vars?: TrVars) => translate(entry, locale, vars), [locale]);
}
